import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import api from '../api/api';
import '../css/AdminSwitches.css';
import { addTrialDays } from '../utils/trialPolicy';

const dateLabel = (value) => value ? value.split('-').reverse().join('/') : 'Not set';

export default function TherapistOrganisationModal({ user, clinics, onClose, onSaved, companyCycleOnly = false }) {
  const [details, setDetails] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [savingTrial, setSavingTrial] = useState(false);
  const [trialEnd, setTrialEnd] = useState('');
  const [trialError, setTrialError] = useState('');
  const [trialMessage, setTrialMessage] = useState('');
  const busy = saving || savingTrial;
  const [selected, setSelected] = useState([]);
  useEffect(() => {
    let active = true;
    api.get(`/organisation-memberships/${user.id}/`).then(({ data }) => {
      if (active) { setDetails(data); setTrialEnd(data.trial_end_date || ''); }
    }).catch(() => {
      if (active) setError('Could not load organisation controls. Your staff account needs organisation administrator permission.');
    });
    return () => { active = false; };
  }, [user.id]);
  const update = (field, value) => setDetails((current) => ({ ...current, [field]: value }));
  const save = async (event) => {
    event.preventDefault();
    if (busy) return;
    setSaving(true); setError('');
    try {
      await api.post(`/organisation-memberships/${user.id}/`, {
        clinic: details.clinic ? Number(details.clinic) : null,
        ...(typeof details.is_clinic_manager === "boolean" ? { is_clinic_manager: Boolean(details.clinic) && details.is_clinic_manager } : {}),
        covered: companyCycleOnly ? Boolean(details.clinic) : details.covered, share_clients: details.share_clients, client_ids: selected,
      });
      onSaved(); onClose();
    } catch (err) {
      const data = err.response?.data;
      setError(typeof data?.detail === 'string' ? data.detail : 'Could not save organisation membership. Check the company subscription and selected clients, then try again.');
    } finally { setSaving(false); }
  };
  const saveTrial = async () => {
    if (busy || !trialEnd) return;
    setSavingTrial(true); setTrialError(''); setTrialMessage('');
    try {
      const { data } = await api.patch(`/organisation-memberships/${user.id}/`, { trial_end_date: trialEnd });
      setDetails((current) => ({ ...current, trial_end_date: data.trial_end_date, access_paused: data.access_paused }));
      setTrialEnd(data.trial_end_date || '');
      setTrialMessage('Trial end date saved.');
      onSaved('Trial end date saved.');
    } catch (err) {
      const data = err.response?.data;
      setTrialError(data?.detail || data?.trial_end_date?.[0] || 'Could not save the trial end date. Please try again.');
    } finally { setSavingTrial(false); }
  };
  const updateTrialEnd = (value) => { setTrialEnd(value); setTrialMessage(''); setTrialError(''); };
  return <Modal show onHide={() => { if (!busy) onClose(); }} size="lg" scrollable>
    <Modal.Header closeButton={!busy}><Modal.Title>Manage organisation: {user.first_name} {user.last_name}</Modal.Title></Modal.Header>
    <Form onSubmit={save} className="d-flex flex-column overflow-hidden" style={{ minHeight: 0 }}>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {!details && !error && <Spinner animation="border" role="status"><span className="visually-hidden">Loading organisation details</span></Spinner>}
        {details && <>
          {!companyCycleOnly && <div className="bg-light rounded p-3 mb-3">
            <strong>Subscription and trial dates</strong>
            <dl className="row mb-0 mt-2">
              <dt className="col-sm-4">Access</dt><dd className="col-sm-8">{details.access_paused ? 'Paused' : 'Available'}</dd>
              <dt className="col-sm-4">Subscription</dt><dd className="col-sm-8">{details.billing_mode === 'clinic' ? 'Company' : details.billing_mode === 'individual' ? 'Individual' : 'Not set'} — {details.billing_state || 'Not set'}</dd>
              <dt className="col-sm-4">Start date</dt><dd className="col-sm-8">{dateLabel(details.start_date)}</dd>
              <dt className="col-sm-4">Trial end date</dt><dd className="col-sm-8">{dateLabel(details.trial_end_date)}</dd>
            </dl>
            {details.billing_state === 'trialing' && <fieldset disabled={busy} className="mt-3">
              <Form.Group controlId="membership-trial-end">
                <Form.Label>Set trial end date</Form.Label>
                <div className="d-flex flex-column flex-sm-row gap-2">
                  <Form.Control type="date" value={trialEnd} min={details.start_date || undefined}
                    aria-describedby="membership-trial-help" onChange={(event) => updateTrialEnd(event.target.value)} />
                  <Button type="button" variant="outline-primary" className="text-nowrap" disabled={!trialEnd}
                    onClick={() => updateTrialEnd(addTrialDays(trialEnd, 7))}>+ 1 week</Button>
                </div>
                <Form.Text id="membership-trial-help" className="d-block mb-2">Adds 7 days to the selected trial end date, or choose a date manually. Changes apply only to this therapist when you save the trial date.</Form.Text>
              </Form.Group>
              <Button type="button" onClick={saveTrial}
                disabled={!trialEnd || trialEnd === details.trial_end_date || (details.start_date && trialEnd < details.start_date)}>
                {savingTrial ? 'Saving trial date…' : 'Save trial date'}
              </Button>
              {trialError && <Alert variant="danger" className="mt-2 mb-0">{trialError}</Alert>}
              {trialMessage && <Alert variant="success" role="status" className="mt-2 mb-0">{trialMessage}</Alert>}
            </fieldset>}
          </div>}
          <fieldset disabled={busy}>
            <Form.Group className="mb-3" controlId="membership-clinic">
              <Form.Label>Organisation</Form.Label>
              <Form.Select value={details.clinic || ''} onChange={(event) => {
                const clinic = event.target.value;
                setDetails((current) => ({ ...current, clinic,
                  is_clinic_manager: (current.manager_clinic_ids || []).includes(Number(clinic)),
                }));
              }}>
                <option value="">Remove organisation membership</option>
                {clinics.map((clinic) => <option key={clinic.id} value={clinic.id}>{clinic.name}</option>)}
              </Form.Select>
            </Form.Group>
            {typeof details.is_clinic_manager === 'boolean' && <Form.Check type="switch" role="switch" className="admin-switch mb-3" id="membership-manager"
              label="Clinic manager" disabled={!details.clinic} checked={details.is_clinic_manager}
              onChange={(event) => update('is_clinic_manager', event.target.checked)} />}
            {!companyCycleOnly && <><Form.Check type="switch" role="switch" className="admin-switch mb-2" id="membership-covered" label="Covered by this company's subscription" checked={details.covered} onChange={(event) => update('covered', event.target.checked)} />
            <Form.Text className="d-block mb-3">Company coverage requires an active or sponsored company subscription. Joining without coverage keeps an individual trial's original dates.</Form.Text></>}
            <Form.Check type="switch" role="switch" id="membership-sharing" className="admin-switch mb-3" label="Share this organisation's clients with this therapist" checked={details.share_clients} onChange={(event) => update('share_clients', event.target.checked)} />
            <h3 className="h6">Transfer trial clients and their programs</h3>
            <p className="small text-muted">Select only the private trial clients to move. Their programs move with them; unselected clients stay private.</p>
            {details.clients.length === 0 && <p>No private trial clients available to transfer.</p>}
            {details.clients.map((client) => <Form.Check type="switch" role="switch" key={client.id} id={`transfer-client-${client.id}`} className="admin-switch mb-2" label={`${client.name} (${client.email}) — ${client.program_count} programs`} checked={selected.includes(client.id)} onChange={(event) => setSelected((current) => event.target.checked ? [...current, client.id] : current.filter((id) => id !== client.id))} />)}
          </fieldset>
        </>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" disabled={busy} onClick={onClose}>Close</Button>
        <Button type="submit" disabled={!details || busy}>{saving ? 'Saving…' : 'Save organisation'}</Button>
      </Modal.Footer>
    </Form>
  </Modal>;
}
